import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light'); // 'light', 'dark', 'auto'
  const [actualTheme, setActualTheme] = useState('light'); // What's actually applied

  // Check if it's daytime (6am - 6pm)
  const isDaytime = () => {
    const hour = new Date().getHours();
    return hour >= 6 && hour < 18;
  };

  // Apply theme to document
  const applyTheme = (themeToApply) => {
    if (themeToApply === 'dark') {
      document.documentElement.classList.add('dark');
      setActualTheme('dark');
    } else {
      document.documentElement.classList.remove('dark');
      setActualTheme('light');
    }
  };

  // Update theme based on preference
  useEffect(() => {
    if (theme === 'auto') {
      const autoTheme = isDaytime() ? 'light' : 'dark';
      applyTheme(autoTheme);

      // Check every hour for auto theme
      const interval = setInterval(() => {
        const newAutoTheme = isDaytime() ? 'light' : 'dark';
        applyTheme(newAutoTheme);
      }, 60 * 60 * 1000); // Every hour

      return () => clearInterval(interval);
    } else {
      applyTheme(theme);
    }
  }, [theme]);

  // Load user's theme preference from backend
  useEffect(() => {
    const loadUserTheme = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${API}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const userTheme = response.data.theme_preference || 'light';
          setTheme(userTheme);
        } catch (error) {
          console.error('Failed to load theme preference:', error);
          // Fallback to localStorage
          const savedTheme = localStorage.getItem('theme') || 'light';
          setTheme(savedTheme);
        }
      } else {
        // Not logged in, use localStorage
        const savedTheme = localStorage.getItem('theme') || 'light';
        setTheme(savedTheme);
      }
    };

    loadUserTheme();
  }, []);

  // Save theme preference
  const changeTheme = async (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);

    const token = localStorage.getItem('token');
    if (token) {
      try {
        await axios.patch(
          `${API}/api/auth/update-theme`,
          { theme_preference: newTheme },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, actualTheme, changeTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
